import { defineStore } from 'pinia';
import { Api } from '@/Api';
import { DEBUG_NAMESPACE, useDebug } from '@/composables/useDebug';

export enum RedactionStatus {
  ENQUEUED = 'enqueued',
  RUNNING = 'running',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export enum RedactionResult {
  REDACTED = 'redacted',
  NOTHING_FOUND = 'nothing_found',
}

export interface RedactionSummary {
  status: RedactionStatus;
  result: RedactionResult | null;
  categories: string[];
  counts: Record<string, number>;
}

export interface RedactionState {
  summary: RedactionSummary | null;
  isStarting: boolean;
}

const { debug } = useDebug(DEBUG_NAMESPACE.STORE_REDACTION);

// Category ids are data, not field names: `counts` is keyed by them and `categories`
// carries them as values. Every wire payload is camelCased on the way in, which would
// leave `counts.awsKey` next to `categories: ['aws_key']`, so the keys are put back.
export const withCategoryIdKeys = (summary?: RedactionSummary | null): RedactionSummary | null => {
  if (!summary) {
    return null;
  }

  const counts = Object.entries(summary.counts || {}).reduce<Record<string, number>>((all, [key, count]) => {
    all[key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)] = count;
    return all;
  }, {});

  return { ...summary, counts };
};

export const useRedactionStore = defineStore('redaction', {
  state: (): RedactionState => ({
    summary: null,
    isStarting: false,
  }),

  getters: {
    isRunning: (state: RedactionState) => state.summary?.status === RedactionStatus.ENQUEUED || state.summary?.status === RedactionStatus.RUNNING,
    isRedacted: (state: RedactionState) => state.summary?.result === RedactionResult.REDACTED,
    hasFailed: (state: RedactionState) => state.summary?.status === RedactionStatus.FAILED,
  },

  actions: {
    initialize(summary?: RedactionSummary | null) {
      this.summary = withCategoryIdKeys(summary);
    },

    patch(summary?: RedactionSummary | null) {
      if (!summary) {
        return;
      }

      this.summary = withCategoryIdKeys(summary);
    },

    // The run itself reports through the item channel, so the response only has to
    // put the viewer into the running state.
    //
    // A second call while the first is in flight is dropped: the run is not visible to
    // the server's own in-progress check until it is created, so two creates can both
    // be accepted and the loser's failure becomes the newest summary.
    async start(itemId: string) {
      if (this.isStarting) {
        return false;
      }

      this.isStarting = true;

      try {
        const response = await Api.item.redaction.create(itemId);
        this.patch(response.data.redaction?.attributes);
        return true;
      } catch (error) {
        debug('[redaction] could not start:', error);
        throw error;
      } finally {
        this.isStarting = false;
      }
    },
  },
});
